import {
  GROUP_LABEL,
  fmtText,
  fmtYears,
  monthLabel,
  MONTH_COUNT,
  type Staff,
  type World,
} from '@kdk/shared';
import { tokens } from '@kdk/shared/theme';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useWorld } from '../../api/queries';
import { AsyncBoundary } from '../../shared-ui/AsyncBoundary';
import { DrawerSkeleton } from '../../shared-ui/skeletons';
import { caseColor } from '../../shared-ui/caseColor';
import { DetailDrawer, type BackLink } from '../../shared-ui/DetailDrawer';
import { TableScroll } from '../../shared-ui/TableScroll';
import { KeyValueList } from '../../shared-ui/KeyValueList';
import { buildStaffDetail } from './model/staffDetail';

const StaffBody = ({ staff, world }: { staff: Staff; world: World }) => {
  const detail = useMemo(() => buildStaffDetail(staff, world.cases), [staff, world.cases]);
  const experience = `${fmtYears(staff.years)}${staff.age === null ? '' : `（${staff.age}歳）`}`;
  const traits = [staff.canBeAgent ? '現場代理人可' : null, staff.hasDcExperience ? 'DC経験' : null]
    .filter((t): t is string => t !== null)
    .map((t) => `・${t}`)
    .join('');

  return (
    <>
      <Typography variant="h2" sx={{ fontSize: 18, mt: '4px', mb: '12px' }}>
        {staff.name}
      </Typography>
      <KeyValueList
        items={[
          { label: '所属', value: `${GROUP_LABEL[staff.group]}・${fmtText(staff.base)}` },
          { label: '経験', value: `${experience}${traits}` },
          {
            label: '資格',
            value: staff.qualifications.length === 0 ? '—' : staff.qualifications.join('、'),
          },
        ]}
      />
      <Box sx={{ borderTop: `1px solid ${tokens.line}`, pt: '12px', mt: '14px' }}>
        <Typography variant="h3" sx={{ color: 'text.secondary', mb: '8px' }}>
          月別の案件配分（%）
        </Typography>
        <TableScroll ariaLabel="月別の案件配分の表" axis="x" variant="plain">
          <Table size="small" sx={{ fontSize: 12.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>案件</TableCell>
                {Array.from({ length: MONTH_COUNT }, (_, m) => (
                  <TableCell key={m} align="right">
                    {monthLabel(m)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.rows.map((row) => (
                <TableRow key={row.caseId}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box
                      component="i"
                      sx={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '2px',
                        mr: '6px',
                        bgcolor: caseColor(row.caseId),
                      }}
                    />
                    {row.caseId}
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {fmtText(row.name)}
                      {row.dedicated ? '（専任）' : ''}｜{fmtText(row.role)}
                    </Typography>
                  </TableCell>
                  {row.percents.map((p, m) => (
                    <TableCell key={m} align="right">
                      {p === null ? (
                        <Box component="span" sx={{ color: tokens.dim }}>
                          ·
                        </Box>
                      ) : (
                        p
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <b>合計</b>
                </TableCell>
                {detail.totals.map((total, m) => (
                  <TableCell
                    key={m}
                    align="right"
                    sx={{ color: total > 100 ? tokens.red : total === 0 ? tokens.dim : undefined }}
                  >
                    <b>{total}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableScroll>
        <Typography sx={{ fontSize: 12, color: tokens.dim, mt: '10px' }}>
          役割は案件ごとに異なる（ある案件では代理人、別の案件では施工管理の応援、など）。
        </Typography>
      </Box>
    </>
  );
};

export const StaffDrawer = ({
  id,
  onClose,
  back,
}: {
  id: string;
  onClose: () => void;
  back?: BackLink | undefined;
}) => {
  const world = useWorld();
  const title =
    world.status === 'success'
      ? (world.data.staff.find((s) => s.id === id)?.name ?? '社員')
      : '社員';
  return (
    <DetailDrawer title={title} onClose={onClose} back={back}>
      <AsyncBoundary state={world} skeleton={<DrawerSkeleton />}>
        {(w) => {
          const staff = w.staff.find((s) => s.id === id);
          return staff === undefined ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              該当する社員が見つかりません。
            </Typography>
          ) : (
            <StaffBody staff={staff} world={w} />
          );
        }}
      </AsyncBoundary>
    </DetailDrawer>
  );
};
